import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { StatusCardWorkspaceService } from '../../shared-services/status-card-workspace.service';
import { ConfigurableDetailFormComponent } from '../configurable-detail-form/configurable-detail-form.component';
import {
  EntityPermissionChange,
  TabbedDetailPanelComponent,
  UserRoleChange,
} from '../tabbed-detail-panel/tabbed-detail-panel.component';

interface StatusCardItem {
  title?: string;
  description?: string;
  meta?: string;
  status?: string;
  icon?: string;
  tags?: Array<{ label?: string; variant?: string }>;
  selected?: boolean;
  detailPanel?: any;
}

@Component({
  selector: 'app-status-card-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, ConfigurableDetailFormComponent, TabbedDetailPanelComponent],
  templateUrl: './status-card-list.component.html',
  styleUrl: './status-card-list.component.scss',
})
export class StatusCardListComponent implements OnChanges, AfterViewInit, OnDestroy {
  private static readonly DARK_LAYOUT_TRACE_KEY = 'DVM_DARK_LAYOUT_TRACE_91B2';
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly configService = inject(ConfigService);
  private readonly workspaceService = inject(StatusCardWorkspaceService);
  private dataSubscription?: Subscription;
  private detailSubscriptions = new Subscription();
  private detailCache = new Map<string, any>();
  private detailLoading = new Set<string>();
  private dirtyEntityNames = new Set<string>();
  private saveSubscription?: Subscription;
  private workspaceActionSubscription?: Subscription;
  private toastTimer?: ReturnType<typeof setTimeout>;
  @Input() data: any;
  activeIndex = 0;
  activeToolbarView: 'globalHierarchy' | null = null;
  private workspaceHierarchyPanel: any = null;
  apiItems: StatusCardItem[] | null = null;
  userRoleChanges: UserRoleChange[] = [];
  entityPermissionChanges: EntityPermissionChange[] = [];
  toastVisible = false;
  toastMessage = '';
  savingChanges = false;
  addRoleDialogOpen = false;
  newRoleName = '';
  newRoleDescription = '';
  newRoleParent = '';
  newEntityDialogOpen = false;
  newEntityName = '';
  newEntityDisplayName = '';
  newEntityType = 'NonTimeSeries';
  newEntityBusiness = true;
  newEntityVersioning = false;
  registryOpen = false;
  registryQuery = '';
  registryEntities: any[] = [];

  ngOnChanges(): void {
    const selectedIndex = this.items.findIndex((item) => item.selected);
    this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    this.queueLayoutTrace('changes');
  }

  ngAfterViewInit(): void {
    if (this.workspaceSource) {
      this.workspaceActionSubscription = this.workspaceService.actions$(this.workspaceSource).subscribe((action) => {
        if (action.type === 'save') this.saveUserRoleChanges(action.payload);
        if (action.type === 'addEntity') this.openNewEntityDialog();
        if (action.type === 'addRole') this.openAddRoleDialog();
        if (action.type === 'detailChanged') this.markActiveEntityDirty();
        if (action.type === 'userRoleChange') this.onUserRoleChange(action.payload);
        if (action.type === 'entityPermissionChange') this.onEntityPermissionChange(action.payload);
        if (action.type === 'registryOptionSelected') this.addRegistryEntity(action.payload);
        if (action.type === 'search') this.onSearch(String(action.payload ?? ''));
        if (action.type === 'statusChange') this.updateActiveStatus(String(action.payload ?? ''));
        if (action.type === 'toggleChange') this.updateActiveTag(action.payload);
        if (action.type === 'detailAction') this.handleDetailAction(String(action.payload ?? ''));
      });
    }
    this.loadItemsFromApi();
    this.queueLayoutTrace('after-view-init');
    setTimeout(() => this.queueLayoutTrace('after-view-init-250ms'), 250);
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.detailSubscriptions.unsubscribe();
    this.saveSubscription?.unsubscribe();
    this.workspaceActionSubscription?.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  get subtitle(): string {
    return String(this.data?.subtitle ?? '').trim();
  }

  get headerTitle(): string {
    return String(this.data?.headerTitle ?? '').trim();
  }

  get headerSubtitle(): string {
    return String(this.data?.headerSubtitle ?? '').trim();
  }

  get toolbar(): any {
    return this.data?.toolbar ?? {};
  }

  get hasToolbarActions(): boolean {
    return !!(
      this.globalHierarchyLabel ||
      this.toolbarSearchPlaceholder ||
      this.addRoleLabel ||
      this.validateLabel ||
      this.saveChangesLabel
      || this.addEntityLabel
    );
  }

  get globalHierarchyLabel(): string {
    return String(this.toolbar?.globalHierarchy?.label ?? '').trim();
  }

  get globalHierarchyPanel(): any {
    return this.workspaceHierarchyPanel ?? this.toolbar?.globalHierarchy?.panel ?? null;
  }

  get isGlobalHierarchyActive(): boolean {
    return this.activeToolbarView === 'globalHierarchy';
  }

  get hierarchyNodes(): any[] {
    return Array.isArray(this.globalHierarchyPanel?.nodes) ? this.globalHierarchyPanel.nodes : [];
  }

  get addRoleLabel(): string {
    return String(this.data?.listActions?.addRole?.label ?? this.toolbar?.addRole?.label ?? '').trim();
  }

  get validateLabel(): string {
    return String(this.toolbar?.validate?.label ?? '').trim();
  }

  get saveChangesLabel(): string {
    return String(this.toolbar?.saveChanges?.label ?? '').trim();
  }

  get toolbarSearchPlaceholder(): string {
    return String(this.toolbar?.registrySearch?.placeholder ?? this.toolbar?.search?.placeholder ?? '').trim();
  }

  get filteredRegistryOptions(): any[] {
    const query = this.registryQuery.trim().toLowerCase();
    const options = Array.isArray(this.toolbar?.registrySearch?.options)
      ? this.toolbar.registrySearch.options
      : this.registryEntities;
    return options.filter((option: any) => {
      const title = String(option?.title ?? option?.name ?? option);
      return !query || title.toLowerCase().includes(query);
    });
  }

  setRegistryQuery(value: string): void {
    this.registryQuery = value;
    this.registryOpen = true;
  }

  addRegistryEntity(option: any): void {
    const title = String(option?.title ?? option?.name ?? option).trim();
    if (!title) return;
    const sourceItems = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    const existingIndex = sourceItems.findIndex((item: StatusCardItem) =>
      String(item.title).toLowerCase() === title.toLowerCase(),
    );
    if (existingIndex >= 0) {
      this.registryQuery = '';
      this.registryOpen = false;
      this.activeIndex = existingIndex;
      this.loadActiveItemDetail();
      return;
    }
    const item: StatusCardItem = {
      title,
      description: String(option?.description ?? title.replace(/_/g, ' ')),
      icon: String(option?.icon ?? 'add_box'),
      status: String(option?.status ?? 'Draft'),
      tags: Array.isArray(option?.tags) ? option.tags : [{ label: 'Business' }, { label: '0 attrs' }],
    };
    this.apiItems = [...sourceItems, item];
    this.searchTerm = '';
    this.registryQuery = '';
    this.registryOpen = false;
    this.activeIndex = this.apiItems.length - 1;
  }

  get items(): StatusCardItem[] {
    const items = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    const query = this.searchTerm.toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item: any) =>
      [item.title, item.description, item.meta, item.status]
        .some((value) => String(value ?? '').toLowerCase().includes(query)),
    );
  }

  searchTerm = '';

  get gap(): string {
    return this.normalizeCssSize(this.data?.gap, '30px');
  }

  get activeItem(): StatusCardItem | null {
    return this.items[this.activeIndex] ?? this.items[0] ?? null;
  }

  get hasDetailPanel(): boolean {
    return this.isStandaloneList || this.isGlobalHierarchyActive || !!this.activeDetailPanel;
  }

  get isStandaloneList(): boolean {
    return !!this.workspaceSource;
  }

  get workspaceSource(): string {
    return String(this.data?.binding?.source ?? '').trim();
  }

  get activeDetailPanel(): any {
    if (this.activeItem?.detailPanel) return this.activeItem.detailPanel;
    const templateItemTitle = String(this.data?.detailPanelTemplateItem ?? '').trim();
    if (templateItemTitle) {
      const configuredItems: StatusCardItem[] = Array.isArray(this.data?.items) ? this.data.items : [];
      const templateItem = configuredItems.find((item) => item.title === templateItemTitle);
      return templateItem?.detailPanel ?? this.data?.detailPanel ?? null;
    }
    return this.activeItem?.detailPanel ?? this.data?.detailPanel ?? null;
  }

  get addEntityLabel(): string {
    return String(this.toolbar?.addEntity?.label ?? '').trim();
  }

  openNewEntityDialog(): void {
    this.newEntityName = '';
    this.newEntityDisplayName = '';
    this.newEntityType = 'NonTimeSeries';
    this.newEntityBusiness = true;
    this.newEntityVersioning = false;
    this.newEntityDialogOpen = true;
  }

  closeNewEntityDialog(): void {
    this.newEntityDialogOpen = false;
  }

  createEntity(): void {
    const title = this.newEntityName.trim();
    if (!title) {
      this.showToast('Entity name is required');
      return;
    }
    const sourceItems = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    if (sourceItems.some((item: StatusCardItem) => String(item.title).toLowerCase() === title.toLowerCase())) {
      this.showToast(`Entity "${title}" already exists`);
      return;
    }
    const tags: Array<{ label?: string; variant?: string }> = [];
    if (this.newEntityBusiness) tags.push({ label: 'Business' });
    if (this.newEntityVersioning) tags.push({ label: 'versioned', variant: 'version' });
    const item: StatusCardItem = {
      title,
      description: this.newEntityDisplayName.trim() || title,
      icon: 'add_box',
      status: 'Draft',
      tags: [...tags, { label: '0 attrs' }],
    };
    (item as any).entityType = this.newEntityType;
    this.detailCache.set(title, {
      attributesList: [], businessRules: [], childEntities: [], deamReads: [],
      displayName: item.description, entity: title, entityType: this.newEntityType,
      businessEntity: this.newEntityBusiness, dataVersioning: this.newEntityVersioning,
    });
    this.dirtyEntityNames.add(title);
    this.apiItems = [...sourceItems, item];
    this.searchTerm = '';
    this.activeIndex = this.apiItems.length - 1;
    this.closeNewEntityDialog();
    this.showToast(`Entity "${title}" created successfully`);
  }

  get searchPlaceholder(): string {
    return String(this.toolbarSearchPlaceholder || this.data?.search?.placeholder || '').trim();
  }

  get detailPanelType(): string {
    return String(this.activeDetailPanel?.type ?? '').trim();
  }

  get globalHierarchyTitle(): string {
    return String(this.globalHierarchyPanel?.title ?? 'Global Role Hierarchy').trim();
  }

  get globalHierarchyDescription(): string {
    return String(this.globalHierarchyPanel?.description ?? '').trim();
  }

  get globalHierarchySideTitle(): string {
    return String(this.globalHierarchyPanel?.sideTitle ?? '').trim();
  }

  get globalHierarchySideDescription(): string {
    return String(this.globalHierarchyPanel?.sideDescription ?? '').trim();
  }

  get globalHierarchyActionLabel(): string {
    return String(this.globalHierarchyPanel?.actionLabel ?? '').trim();
  }

  activateItem(index: number): void {
    this.activeIndex = index;
    this.loadActiveItemDetail();
    this.publishActiveDetail();
  }

  preloadItemDetail(item: StatusCardItem): void {
    this.loadItemDetail(item);
  }

  updateActiveStatus(status: string): void {
    if (this.activeItem) {
      this.activeItem.status = status;
      this.markActiveEntityDirty();
    }
  }

  updateActiveTag(change: { tag: string; checked: boolean }): void {
    const activeItem = this.activeItem;
    if (!activeItem) return;

    const tags = Array.isArray(activeItem.tags) ? activeItem.tags : [];
    const matchingTag = (tag: { label?: string }) =>
      String(tag?.label ?? '').toLowerCase() === change.tag.toLowerCase();
    activeItem.tags = change.checked
      ? (tags.some(matchingTag) ? tags : [...tags, { label: change.tag, variant: change.tag === 'versioned' ? 'version' : undefined }])
      : tags.filter((tag) => !matchingTag(tag));
    this.markActiveEntityDirty();
  }

  markActiveEntityDirty(): void {
    const title = String(this.activeItem?.title ?? '').trim();
    if (title) this.dirtyEntityNames.add(title);
  }

  handleDetailAction(action: string): void {
    if (action === 'duplicate') this.duplicateActiveItem();
    if (action === 'delete') this.deleteActiveItem();
  }

  private duplicateActiveItem(): void {
    const activeItem = this.activeItem;
    if (!activeItem) return;

    const sourceItems = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    const existingTitles = new Set(sourceItems.map((item: StatusCardItem) => String(item.title).toLowerCase()));
    const baseTitle = `${activeItem.title || 'entity'}_copy`;
    let title = baseTitle;
    let copyNumber = 2;
    while (existingTitles.has(title.toLowerCase())) title = `${baseTitle}_${copyNumber++}`;

    const copy: StatusCardItem = JSON.parse(JSON.stringify(activeItem));
    copy.title = title;
    copy.status = 'Draft';
    copy.selected = false;
    const sourceDetail = this.detailCache.get(String(activeItem.title));
    if (sourceDetail) {
      const copiedDetail = JSON.parse(JSON.stringify(sourceDetail));
      copiedDetail.entity = title;
      this.detailCache.set(title, copiedDetail);
    }
    this.dirtyEntityNames.add(title);
    this.apiItems = [...sourceItems, copy];
    this.searchTerm = '';
    this.activeIndex = this.apiItems.length - 1;
    this.publishActiveDetail();
    this.showToast(`Entity "${title}" duplicated successfully`);
  }

  private deleteActiveItem(): void {
    const activeItem = this.activeItem;
    if (!activeItem) return;

    const sourceItems = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    const itemIndex = sourceItems.indexOf(activeItem);
    const remainingItems = sourceItems.filter((item: StatusCardItem) => item !== activeItem);
    this.apiItems = remainingItems;
    this.searchTerm = '';
    this.activeIndex = Math.max(0, Math.min(itemIndex, remainingItems.length - 1));
    this.publishActiveDetail();
    this.showToast(`Entity "${activeItem.title || 'Entity'}" deleted`);
  }

  getItemClasses(item: StatusCardItem, index: number): Record<string, boolean> {
    return {
      selected: index === this.activeIndex,
    };
  }

  onSearch(value: string): void {
    this.searchTerm = value;
    this.activeIndex = 0;
    this.queueLayoutTrace('search');
  }

  toggleGlobalHierarchy(panel?: any): void {
    if (panel) this.workspaceHierarchyPanel = panel;
    if (!this.globalHierarchyPanel) {
      return;
    }
    this.activeToolbarView = this.isGlobalHierarchyActive ? null : 'globalHierarchy';
    this.queueLayoutTrace('global-hierarchy');
  }

  openAddRoleDialog(): void {
    this.newRoleName = '';
    this.newRoleDescription = '';
    this.newRoleParent = '';
    this.addRoleDialogOpen = true;
  }

  closeAddRoleDialog(): void {
    this.addRoleDialogOpen = false;
  }

  createRole(): void {
    const title = this.newRoleName.trim();
    if (!title) {
      this.showToast('Role name is required');
      return;
    }
    const sourceItems = this.apiItems ?? (Array.isArray(this.data?.items) ? this.data.items : []);
    if (sourceItems.some((item: StatusCardItem) => String(item.title).toLowerCase() === title.toLowerCase())) {
      this.showToast(`${title} role already exists`);
      return;
    }
    const item: StatusCardItem = {
      title,
      description: this.newRoleDescription.trim(),
      meta: this.newRoleParent ? `Inherits · ${this.newRoleParent}` : 'Root Role',
      status: 'Active',
    };
    this.apiItems = [...sourceItems, item];
    this.searchTerm = '';
    this.activeIndex = this.apiItems.length - 1;
    this.closeAddRoleDialog();
    this.showToast(`${title} role added`);
  }

  onUserRoleChange(change: UserRoleChange): void {
    const existingIndex = this.userRoleChanges.findIndex((item) => item.user_name === change.user_name);
    this.userRoleChanges = existingIndex >= 0
      ? this.userRoleChanges.map((item, index) => index === existingIndex ? change : item)
      : [...this.userRoleChanges, change];
  }

  onEntityPermissionChange(change: EntityPermissionChange): void {
    const existingIndex = this.entityPermissionChanges.findIndex(
      (item) => item.userRole.toLowerCase() === change.userRole.toLowerCase(),
    );
    this.entityPermissionChanges = existingIndex >= 0
      ? this.entityPermissionChanges.map((item, index) => index === existingIndex ? change : item)
      : [...this.entityPermissionChanges, change];
  }

  saveUserRoleChanges(saveConfigOverride?: any): void {
    const saveConfig = saveConfigOverride ?? this.toolbar?.saveChanges;
    if (!saveConfig?.apiUrl || this.savingChanges) return;
    if (saveConfig.entityMode) {
      this.saveDirtyEntities(saveConfig);
      return;
    }
    if (!this.userRoleChanges.length && !this.entityPermissionChanges.length) {
      this.showToast('No changes to save');
      return;
    }

    const requests: Observable<any>[] = [];
    if (this.userRoleChanges.length) {
      const userRoles = this.userRoleChanges.map((change) => ({
        user_name: change.user_name,
        user_email: change.user_email,
        roles: change.roles.length === 1 ? change.roles[0] : change.roles,
      }));
      const userRequest = this.configService.getConfig(
        saveConfig.apiUrl,
        { ...saveConfig, skipDefaultPayload: true, customPayload: { userRoles } },
        {},
        saveConfig.method || 'POST',
      );
      if (userRequest) requests.push(userRequest);
    }

    this.entityPermissionChanges.forEach((change) => {
      const entityRequest = this.configService.getConfig(
        saveConfig.entityPermissionApiUrl,
        {
          skipDefaultPayload: true,
          customPayload: {
            userRole: [change.userRole],
            entityPermissions: change.entityPermissions,
          },
        },
      );
      if (entityRequest) requests.push(entityRequest);
    });

    this.savingChanges = true;
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = forkJoin(requests).subscribe({
      next: () => {
        this.savingChanges = false;
        this.showToast('User role changes saved successfully');
      },
      error: (error) => {
        this.savingChanges = false;
        this.showToast('Failed to save user role changes');
        console.error('StatusCardList: failed to save user role changes', error);
      },
    });
  }

  private showToast(message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMessage = message;
    this.toastVisible = true;
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = undefined;
    }, 3000);
  }

  private loadItemsFromApi(): void {
    if (!this.data?.apiUrl) {
      return;
    }

    this.apiItems = [];
    this.dataSubscription?.unsubscribe();
    this.dataSubscription = this.configService.getConfig(this.data.apiUrl, this.data, {})?.subscribe({
      next: (res: any) => {
        const titles = this.extractTitles(res);
        if (!titles.length) {
          return;
        }

        this.registryEntities = titles.map((title) => ({ title, icon: 'add_box' }));
        this.apiItems = titles.map((title, index) => this.createTitleCard(title, index));
        this.activeIndex = 0;
        this.loadAllItemDetails(titles);
        this.publishActiveDetail();
        this.queueLayoutTrace('api-items');
      },
      error: (error) => {
        this.apiItems = [];
        this.registryEntities = [];
        console.error('StatusCardList: failed to load items', error);
      },
    });
  }

  private extractTitles(response: any): string[] {
    const source = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.entities)
          ? response.entities
        : Array.isArray(response?.titles)
          ? response.titles
          : [];

    return source
      .map((title: any) => String(title ?? '').trim())
      .filter((title: string) => !!title);
  }

  private saveDirtyEntities(saveConfig: any): void {
    const dirtyNames = [...this.dirtyEntityNames];
    if (!dirtyNames.length) {
      this.showToast('No changes to save');
      return;
    }
    const requests = dirtyNames.map((name) => {
      const item = this.apiItems?.find((candidate) => String(candidate.title) === name);
      const deam = this.buildDeamPayload(item, this.detailCache.get(name));
      return this.configService.getConfig(
        saveConfig.apiUrl,
        { ...saveConfig, skipDefaultPayload: true, customPayload: { deam, entityName: name } },
        {},
        saveConfig.method || 'PUT',
      );
    }).filter((request): request is Observable<any> => !!request);
    if (!requests.length) return;
    this.savingChanges = true;
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = forkJoin(requests).subscribe({
      next: () => {
        dirtyNames.forEach((name) => this.dirtyEntityNames.delete(name));
        this.savingChanges = false;
        this.showToast(`${dirtyNames.length} ${dirtyNames.length === 1 ? 'entity' : 'entities'} saved successfully`);
      },
      error: (error) => {
        this.savingChanges = false;
        this.showToast(this.resolveApiErrorMessage(error, 'Failed to save entity changes'));
        console.error('StatusCardList: failed to save DEAM changes', error);
      },
    });
  }

  private buildDeamPayload(item: StatusCardItem | undefined, cached: any): any {
    const deam = JSON.parse(JSON.stringify(cached || {}));
    deam.entity = item?.title ?? deam.entity;
    deam.displayName = item?.description ?? deam.displayName ?? deam.entity;
    deam.attributesList = Array.isArray(deam.attributesList) ? deam.attributesList : [];
    deam.businessRules = Array.isArray(deam.businessRules) ? deam.businessRules : [];
    deam.childEntities = Array.isArray(deam.childEntities) ? deam.childEntities : [];
    deam.deamReads = Array.isArray(deam.deamReads) ? deam.deamReads : [];
    const tags = item?.tags || [];
    deam.businessEntity = tags.some((tag) => String(tag.label).toLowerCase() === 'business');
    deam.dataVersioning = tags.some((tag) => String(tag.label).toLowerCase() === 'versioned');
    const entityFields = item?.detailPanel?.sections?.find((section: any) => section.title === 'Entity Info')?.fields || [];
    entityFields.forEach((field: any) => {
      if (field.label === 'Entity Name' && field.value) deam.entity = field.value;
      if (field.label === 'Display Name') deam.displayName = field.value;
      if (field.label === 'Entity Type') deam.entityType = field.value;
    });
    const attributeRows = item?.detailPanel?.sections?.find((section: any) => section.title === 'Attributes')?.rows;
    if (Array.isArray(attributeRows)) {
      deam.attributesList = attributeRows.map((row: any) => {
        const fields = row?.fields || [];
        const name = fields[1]?.value || '';
        const original = (deam.attributesList || []).find((attribute: any) => attribute.name === name) || {};
        return {
          ...original,
          type: fields[0]?.value || original.type || 'string',
          name,
          displayName: fields[2]?.value || name,
          mandatory: !!fields[3]?.checked,
          unique: !!fields[4]?.checked,
          indexed: !!fields[5]?.checked,
          businessKey: !!fields[6]?.checked,
          enableUpdate: fields[7]?.checked !== false,
        };
      });
    }
    return deam;
  }

  private resolveApiErrorMessage(error: any, fallback: string): string {
    return String(
      error?.error?.message
      ?? error?.error?.error
      ?? error?.message
      ?? fallback,
    );
  }

  private loadActiveItemDetail(): void {
    const item = this.activeItem;
    if (item) this.loadItemDetail(item);
  }

  private loadAllItemDetails(titles: string[]): void {
    const request = this.data?.detailRequest;
    const uniqueTitles = [...new Set(titles.map((title) => String(title).trim()).filter(Boolean))];
    if (!request?.apiUrl || !uniqueTitles.length) return;

    const missingTitles = uniqueTitles.filter((title) => !this.detailCache.has(title));
    if (!missingTitles.length) return;
    missingTitles.forEach((title) => this.detailLoading.add(title));

    const requestSubscription = this.configService.getConfig(
      request.apiUrl,
      { ...request, skipDefaultPayload: true, customPayload: { entity: missingTitles } },
      {},
      request.method || 'POST',
    )?.subscribe({
      next: (response: any) => {
        missingTitles.forEach((title) => this.detailLoading.delete(title));
        const deams = Array.isArray(response?.deams)
          ? response.deams
          : Array.isArray(response?.data?.deams)
            ? response.data.deams
            : [];
        deams.forEach((entry: any) => {
          const title = String(entry?.entityName ?? entry?.deam?.entity ?? '').trim();
          const detail = entry?.deam ?? entry;
          if (!title || !detail) return;
          this.detailCache.set(title, detail);
          const item = this.apiItems?.find((candidate) =>
            String(candidate.title).toLowerCase() === title.toLowerCase(),
          );
          if (item) this.applyDeamDetail(item, detail);
        });
      },
      error: (error) => {
        missingTitles.forEach((title) => this.detailLoading.delete(title));
        console.error('StatusCardList: failed to bulk-load DEAM details', error);
      },
    });
    if (requestSubscription) this.detailSubscriptions.add(requestSubscription);
  }

  private loadItemDetail(item: StatusCardItem): void {
    const request = this.data?.detailRequest;
    const title = String(item?.title ?? '').trim();
    if (!title || !request?.apiUrl) return;

    // Preserve the entity's unsaved in-memory form state while switching cards.
    // A fresh component instance (page navigation) naturally resets this state.
    if (item.detailPanel) return;

    const cached = this.detailCache.get(title);
    if (cached) {
      this.applyDeamDetail(item, cached);
      return;
    }

    if (this.detailLoading.has(title)) return;
    this.detailLoading.add(title);

    const requestSubscription = this.configService.getConfig(
      request.apiUrl,
      { ...request, skipDefaultPayload: true, customPayload: { entity: [title] } },
      {},
      request.method || 'POST',
    )?.subscribe({
      next: (response: any) => {
        this.detailLoading.delete(title);
        const details = this.extractDeamDetail(response, title);
        if (!details) return;
        this.detailCache.set(title, details);
        this.applyDeamDetail(item, details);
      },
      error: (error) => {
        this.detailLoading.delete(title);
        console.error(`StatusCardList: failed to load DEAM details for ${title}`, error);
      },
    });
    if (requestSubscription) this.detailSubscriptions.add(requestSubscription);
  }

  private extractDeamDetail(response: any, title: string): any {
    const deams = Array.isArray(response?.deams) ? response.deams : Array.isArray(response?.data?.deams) ? response.data.deams : [];
    const match = deams.find((entry: any) =>
      String(entry?.entityName ?? entry?.deam?.entity ?? '').toLowerCase() === title.toLowerCase(),
    );
    return match?.deam ?? match ?? null;
  }

  private applyDeamDetail(item: StatusCardItem, deam: any): void {
    const templateTitle = String(this.data?.detailPanelTemplateItem ?? '').trim();
    const configuredItems: StatusCardItem[] = Array.isArray(this.data?.items) ? this.data.items : [];
    const template = configuredItems.find((entry) => entry.title === templateTitle)?.detailPanel ?? this.data?.detailPanel;
    if (!template) return;

    const panel = JSON.parse(JSON.stringify(template));
    const attributes = Array.isArray(deam?.attributesList) ? deam.attributesList : [];
    item.description = String(deam?.displayName ?? item.description ?? item.title);
    item.tags = [
      ...(item.tags || []).filter((tag) => !String(tag.label).includes('attrs')),
      { label: `${attributes.length} attrs` },
    ];

    const entitySection = panel.sections?.find((section: any) => section.title === 'Entity Info');
    (entitySection?.fields || []).forEach((field: any) => {
      if (field.label === 'Entity Name') field.value = deam?.entity ?? item.title;
      if (field.label === 'Display Name') field.value = deam?.displayName ?? item.description;
    });

    const attributeSection = panel.sections?.find((section: any) => section.title === 'Attributes');
    if (attributeSection) {
      attributeSection.count = attributes.length;
      const typeCounts = attributes.reduce((counts: Record<string, number>, attribute: any) => {
        const type = String(attribute?.type || 'string').toLowerCase();
        counts[type] = (counts[type] || 0) + 1;
        return counts;
      }, {});
      const typeOrder = ['array', 'json', 'string', 'integer', 'long', 'datetime', 'numeric', 'boolean'];
      attributeSection.headerFields = Object.keys(typeCounts)
        .sort((left, right) => {
          const leftIndex = typeOrder.indexOf(left);
          const rightIndex = typeOrder.indexOf(right);
          return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex);
        })
        .map((type) => ({
          type: 'badge',
          value: `${this.attributeTypeAbbreviation(type)}·${typeCounts[type]}`,
          variant: this.attributeTypeVariant(type),
        }));
      attributeSection.rows = attributes.map((attribute: any) => ({
        fields: [
          {
            type: 'badge',
            value: attribute.type || 'string',
            variant: this.attributeTypeVariant(String(attribute.type || 'string').toLowerCase()),
          },
          { type: 'label', value: attribute.name || '' },
          { type: 'label', value: attribute.displayName || attribute.name || '' },
          { type: 'checkbox', checked: !!attribute.mandatory },
          { type: 'checkbox', checked: !!attribute.unique },
          { type: 'checkbox', checked: !!attribute.indexed },
          { type: 'checkbox', checked: !!attribute.businessKey },
          { type: 'toggle', checked: attribute.enableUpdate !== false },
        ],
      }));
    }
    item.detailPanel = panel;
    if (item === this.activeItem) this.publishActiveDetail();
  }

  private publishActiveDetail(): void {
    if (!this.workspaceSource || !this.activeItem) return;
    this.workspaceService.publish(this.workspaceSource, {
      item: this.activeItem,
      detailPanel: this.activeDetailPanel,
      items: this.items,
    });
  }

  private attributeTypeAbbreviation(type: string): string {
    const abbreviations: Record<string, string> = {
      array: 'arr', json: 'jso', string: 'str', integer: 'int', long: 'lon',
      datetime: 'dat', numeric: 'num', number: 'num', boolean: 'boo', decimal: 'dec',
    };
    return abbreviations[type] || type.slice(0, 3);
  }

  private attributeTypeVariant(type: string): string | undefined {
    if (['array', 'json', 'datetime'].includes(type)) return 'date';
    if (['integer', 'boolean'].includes(type)) return 'version';
    if (['long', 'numeric', 'number', 'decimal'].includes(type)) return 'numeric';
    return undefined;
  }

  private createTitleCard(title: string, index: number): StatusCardItem {
    const details = this.getTitleDetails(title);

    return {
      ...details,
      title,
      selected: index === 0,
    };
  }

  private getTitleDetails(title: string): Partial<StatusCardItem> {
    const detailsByTitle = this.data?.itemDetailsByTitle || this.data?.titleDetails || {};
    const matchedKey = Object.keys(detailsByTitle).find(
      (key) => key.toLowerCase() === title.toLowerCase(),
    );
    return matchedKey ? detailsByTitle[matchedKey] : this.getDefaultTitleDetails();
  }

  private getDefaultTitleDetails(): Partial<StatusCardItem> {
    return this.data?.defaultItemDetails || {
      description: 'Access profile available from source system',
      meta: 'API managed item',
      status: 'Active',
    };
  }

  private queueLayoutTrace(reason: string): void {
    if (typeof window === 'undefined') return;
    requestAnimationFrame(() => this.logLayoutTrace(reason));
  }

  private logLayoutTrace(reason: string): void {
    const host = this.hostElement.nativeElement;
    const root = host.querySelector('.status-card-list') as HTMLElement | null;
    const layout = host.querySelector('.status-card-list__layout') as HTMLElement | null;
    const left = host.querySelector('.status-card-list__left') as HTMLElement | null;
    const items = host.querySelector('.status-card-list__items') as HTMLElement | null;
    const detail = host.querySelector('app-configurable-detail-form, app-tabbed-detail-panel') as HTMLElement | null;
    const rect = (el: HTMLElement | null) => {
      if (!el) return null;
      const box = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      return {
        h: Math.round(box.height),
        w: Math.round(box.width),
        top: Math.round(box.top),
        bottom: Math.round(box.bottom),
        scrollH: el.scrollHeight,
        clientH: el.clientHeight,
        overflowY: style.overflowY,
        heightStyle: style.height,
        maxHeight: style.maxHeight,
        display: style.display,
      };
    };

    console.warn(StatusCardListComponent.DARK_LAYOUT_TRACE_KEY, {
      source: 'status-card-list',
      reason,
      widgetId: this.data?.widgetId ?? null,
      type: this.data?.type ?? null,
      dark: !!document.querySelector('.dark-theme, .dvm-dark-theme, .nav-bar.dark-theme'),
      viewport: {
        innerH: window.innerHeight,
        innerW: window.innerWidth,
        visualH: Math.round(window.visualViewport?.height || 0),
        dvmNoScroll: document.documentElement.classList.contains('dvm-no-scroll'),
      },
      elements: {
        host: rect(host),
        root: rect(root),
        layout: rect(layout),
        left: rect(left),
        items: rect(items),
        detail: rect(detail),
      },
    });
  }

  private normalizeCssSize(value: unknown, fallback: string): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}px`;
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    return fallback;
  }
}
